import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO';
import { preserveUTMParams, trackMarketingEvent } from '../../utils/utmTracking';
import './styles.css';

const API_BASE_URL = process.env.REACT_APP_API_BASE_URL || 'https://api-eternavaults.onrender.com';

export default function SignUp() {
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [company, setCompany] = useState('');
  const [role, setRole] = useState('');
  const [organizationType, setOrganizationType] = useState('');
  const [message, setMessage] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  // Track page view and preserve UTM parameters
  useEffect(() => {
    preserveUTMParams();
    trackMarketingEvent('page_view', {
      page: 'signup',
    });
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    
    // Validate required fields
    if (!email || !firstName || !lastName) {
      setError('Please complete all required fields to continue.');
      return;
    }

    // Validate first name (minimum 2 characters)
    const trimmedFirstName = firstName.trim();
    if (trimmedFirstName.length < 2) {
      setError('Given name must be at least 2 characters.');
      return;
    }

    // Validate last name (minimum 2 characters)
    const trimmedLastName = lastName.trim();
    if (trimmedLastName.length < 2) {
      setError('Family name must be at least 2 characters.');
      return;
    }

    // Basic email validation
    const emailRegex = /^[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}$/i;
    if (!emailRegex.test(email)) {
      setError('Please provide a valid email address for your invitation.');
      return;
    }

    setLoading(true);

    try {
      const response = await fetch(`${API_BASE_URL}/api/access-request`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          email: email.trim(),
          name: `${trimmedFirstName} ${trimmedLastName}`.trim(), // Combine first and last name
          firstName: trimmedFirstName,
          lastName: trimmedLastName,
          company: company ? company.trim() : '',
          role: role ? role.trim() : '',
          organizationType: organizationType || '',
          message: message.trim()
        })
      });

      const data = await response.json();

      if (response.ok && data.success) {
        // Track signup event
        trackMarketingEvent('signup', {
          email: email.substring(0, 3) + '***', // Partial email for privacy
          autoApproved: data.autoApproved,
        });

        // If auto-approved, show immediate success message
        if (data.autoApproved) {
          navigate('/access-request-confirmation', { 
            state: { 
              email, 
              name: `${trimmedFirstName} ${trimmedLastName}`.trim(),
              requestToken: data.requestToken,
              autoApproved: true,
              message: 'Your access request has been approved! Please check your email for your account setup invitation.'
            } 
          });
        } else {
          // Navigate to confirmation page for pending requests
          navigate('/access-request-confirmation', { 
            state: { 
              email, 
              name: `${trimmedFirstName} ${trimmedLastName}`.trim(),
              requestToken: data.requestToken,
              autoApproved: false
            } 
          });
        }
      } else {
        setError(data.error || 'We couldn\'t process your request. Please review the information and try again.');
      }
    } catch (error) {
      console.error('Error submitting access request:', error);
      setError('We couldn\'t process your request at this time. Please try again shortly.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      <SEO
        title="Request Private Access to EternaVaults - Preserve Your Family Legacy"
        description="Request access to EternaVaults and start preserving your family's digital legacy. Secure family vault with emergency access, digital inheritance planning, and bank-level security."
        keywords="eternavaults access request, request digital vault access, family vault access, digital legacy access, secure family storage access"
        canonical="https://eternavaults.com/signup"
        ogImage="/signup-og-image.jpg"
      />
      <div className="login-bg">
        <div className="login-container">
          <div className="login-brand">
            <h1>EternaVaults</h1>
            <p className="login-tagline">Legacy, Secured</p>
            <span className="login-tagline-small">Private digital vaults for discerning families and trusted advisors.<br />
            By invitation only.</span>
          </div>
          <form className="login-form" onSubmit={handleSubmit}>
            <h2>Request Private Access</h2>
            <p className="request-access-intro">
              EternaVaults is a private platform for families who value privacy, continuity, and multi-generational planning.
              We review all access requests to maintain a curated community.
            </p>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1rem' }}>
              <div>
                <label>Given Name *</label>
                <input
                  type="text"
                  placeholder="Given Name"
                  value={firstName}
                  onChange={e => setFirstName(e.target.value)}
                  autoFocus
                  required
                  minLength={2}
                  disabled={loading}
                  style={{ width: '100%' }}
                />
              </div>
              <div>
                <label>Family Name *</label>
                <input
                  type="text"
                  placeholder="Family Name"
                  value={lastName}
                  onChange={e => setLastName(e.target.value)}
                  required
                  minLength={2}
                  disabled={loading}
                  style={{ width: '100%' }}
                />
              </div>
            </div>
            <label>Contact Email *</label>
            <input
              type="email"
              placeholder="your.email@example.com"
              value={email}
              onChange={e => setEmail(e.target.value)}
              required
              disabled={loading}
            />
            <label>Company / Organization</label>
            <input
              type="text"
              placeholder="Law Firm, Family Office, etc."
              value={company}
              onChange={e => setCompany(e.target.value)}
              disabled={loading}
            />
            <label>Your Role</label>
            <input
              type="text"
              placeholder="Partner, Advisor, Wealth Manager, etc."
              value={role}
              onChange={e => setRole(e.target.value)}
              disabled={loading}
            />
            <label>Organization Type</label>
            <select
              value={organizationType}
              onChange={e => setOrganizationType(e.target.value)}
              disabled={loading}
              style={{
                background: 'rgba(191, 169, 122, 0.1)',
                border: '1px solid #bfa97a',
                borderRadius: '8px',
                padding: '0.75rem',
                color: '#e2c89c',
                fontFamily: 'Cormorant Garamond, serif',
                fontSize: '1rem',
                width: '100%',
                boxSizing: 'border-box'
              }}
            >
              <option value="">Select type...</option>
              <option value="law_firm">Law Firm (Family/Estate Focus)</option>
              <option value="family_office">Family Office</option>
              <option value="wealth_manager">Wealth Management Firm</option>
              <option value="financial_advisor">Financial Advisor</option>
              <option value="trust_company">Trust Company</option>
              <option value="estate_planner">Estate Planning Professional</option>
              <option value="private_family">Private Family</option>
              <option value="other">Other</option>
            </select>
            <label>Message (Optional)</label>
            <textarea
              placeholder="Tell us why you're interested in EternaVaults..."
              value={message}
              onChange={e => setMessage(e.target.value)}
              rows="4"
              disabled={loading}
            />
            {error && <div className="error-message">{error}</div>}
            <button 
              type="submit" 
              className="signup-button"
              disabled={loading}
            >
              {loading ? 'Submitting Request...' : 'Request Private Access'}
            </button>
            <div className="login-links">
              <a href="/login">Already have an account? Sign in</a>
            </div>
            <p className="request-access-note">
              We'll review your request within 24-48 hours. If approved, you'll receive an invitation email to complete your account setup.
            </p>
          </form>
        </div>
      </div>
    </>
  );
}
